import { KeyboardEvent } from 'react';

import { Box, SxProps, Theme, Typography } from '@mui/material';

type DotBadgeBaseProps = Readonly<{
  dotColor: string;
  /** Whether the badge is clickable with hover/focus states */
  interactive?: boolean;
  /** Active state; the dot greys when false, and interactive badges also dim */
  isActive?: boolean;
  onClick?: () => void;
  id?: string;
}>;

/** Either a visible label, or a dot on its own named by `ariaLabel`. */
export type DotBadgeProps = DotBadgeBaseProps &
  (
    | Readonly<{ label: string; ariaLabel?: string }>
    | Readonly<{ label?: undefined; ariaLabel: string }>
  );

/**
 * Badge with a coloured status dot and a neutral label.
 */
export const DotBadge = ({
  label,
  ariaLabel,
  dotColor,
  interactive = false,
  isActive = true,
  onClick,
  id,
}: DotBadgeProps) => {
  const handleKeyDown = (e: KeyboardEvent) => {
    if (interactive && onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <Box
      id={id}
      onClick={interactive ? onClick : undefined}
      // A bare div is `role="generic"`, which is not named by `aria-label`.
      role={interactive ? 'checkbox' : label ? undefined : 'img'}
      aria-checked={interactive ? isActive : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
      aria-label={label ? undefined : ariaLabel}
      sx={getBadgeContainerSx(interactive, isActive, label !== undefined)}
    >
      <Box sx={getDotSx(dotColor, isActive)} aria-hidden="true" />
      {label ? <Typography sx={getBadgeTextSx(interactive)}>{label}</Typography> : null}
    </Box>
  );
};

const getBadgeContainerSx =
  (interactive: boolean, isActive: boolean, hasLabel: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-xs'],
    px: hasLabel ? backrs.spacing['spacing-md'] : backrs.spacing['spacing-xxs'],
    py: backrs.spacing['spacing-xxs'],
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
    borderRadius: backrs.radius['radius-sm'],

    ...(interactive && {
      cursor: 'pointer',
      transition: 'opacity 0.2s ease',
      opacity: isActive ? 1 : 0.5,
      '&:hover': {
        opacity: isActive ? 0.9 : 0.7,
      },
      '&:focus': {
        outline: `2px solid ${backrs.colors['Colors/Border/border-brand']}`,
        outlineOffset: '2px',
      },
    }),
  });

const getDotSx =
  (color: string, isActive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '6px',
    height: '6px',
    borderRadius: backrs.radius['radius-full'],
    backgroundColor: isActive ? color : backrs.colors['Colors/Border/border-primary'],
    flexShrink: 0,
  });

const getBadgeTextSx =
  (interactive: boolean): SxProps<Theme> =>
  ({ backrs, typography }) => ({
    ...typography['text-sm-medium'],
    color: backrs.colors['Colors/Text/text-secondary'],

    ...(interactive && {
      lineHeight: '20px',
    }),
  });
